import { memo } from "react";
import { ProductClassToggleGroup as ProductClassToggleGroupBase } from "@atlas-int/controls";
import type { ClassificationConfig } from "@atlas-int/controls";
import { useCurrentVizType } from "../../hooks/useCurrentVizType";
import { useFilteredClassifications, useClassificationConfig } from "../../hooks/useClassificationConfig";
import { useDataAvailability } from "@atlas-int/data";
import { ToggleGroupSkeleton } from "@atlas-int/ui";
import { useExploreParams } from "../../visualization/paramSchemas";
import { ProductClass, ProductLevel } from "@atlas-int/data/types";
import { allProductsDatum } from "@atlas-int/data";
import { vizConstraints } from "@atlas-int/data/schemas";
import { googleAnalyticsCustomEvent } from "../../sharedUtilities/analytics";

const ProductClassToggleGroup = () => {
  const vizType = useCurrentVizType();
  const [
    {
      productClass,
      productLevel,
      year,
    },
    setQuery,
  ] = useExploreParams();

  const { getYearRangeForProductClass } = useDataAvailability();
  const { loading: classificationsLoading } = useClassificationConfig();
  const availableClassifications = useFilteredClassifications();

  const constraints = vizConstraints[vizType];

  if (classificationsLoading || availableClassifications.length === 0) {
    return <ToggleGroupSkeleton />;
  }

  const handleSelect = (value: string) => {
    if (productClass === value) return;

    googleAnalyticsCustomEvent("viz", "change", "productClass", "explore");

    const newSearch: Record<string, unknown> = {};
    const yearRange = getYearRangeForProductClass(value as ProductClass);

    if (yearRange) {
      const { yearMin, yearMax } = yearRange;
      newSearch.startYear = yearMin;
      newSearch.endYear = yearMax;

      if (year && year < yearMin) {
        newSearch.year = yearMin;
      } else if (year && year > yearMax) {
        newSearch.year = yearMax;
      } else if (!year) {
        newSearch.year = yearMax;
      }
    }

    // SITC doesn't support 6-digit; fall back to 4-digit
    if (
      value === ProductClass.SITCProducts &&
      productLevel === ProductLevel.Product6digit
    ) {
      newSearch.productLevel = ProductLevel.Product4digit;
    }

    newSearch.productClass = value;
    newSearch.product = allProductsDatum.productId;
    setQuery(newSearch);
  };

  const isDisabled = (config: ClassificationConfig) => {
    // 6-digit products not available for SITC
    if (
      productLevel === ProductLevel.Product6digit &&
      !config.supportsAllProductLevels
    ) {
      return true;
    }
    // Check centralized viz constraints for allowed product classes
    if (
      constraints?.allowedProductClasses &&
      !constraints.allowedProductClasses.includes(config.value as ProductClass)
    ) {
      return true;
    }
    return false;
  };

  return (
    <ProductClassToggleGroupBase
      value={productClass}
      onChange={handleSelect}
      classifications={availableClassifications}
      isDisabled={isDisabled}
    />
  );
};

export default memo(ProductClassToggleGroup);
